





















































































import Vue from 'vue'
import { defineComponent, computed, toRefs, ref } from '@vue/composition-api'
import store from '@/store'
import { postScribeSync } from '@/api'
import { BLOCK_VALUES, NEW_PROGRAM_REQUISITE_ID, SCRIBE_VALIDATIONS } from '@shared/integration/scribeConstants'
import useDynamicForm from '@/components/DynamicForms/useDynamicForm'
import useVuelidate from '@vuelidate/core'
import type { MergeReport } from '@shared/integration/types'
import type { FormQuestion } from '@shared/templates/types'
import _ from 'lodash'
import type { ScribeSyncResponse } from '@shared/integration/subdomains/scribeSync/scribeSync'

const DynamicForm = () => import('@/components/DynamicForms/DynamicForm.vue')
const CommonCodeEditor = () => import('@/components/Common/CommonCodeEditor.vue')

type ScribeError = {
  errorCode: string,
  condition?: string,
  courseId?: string,
  courseSetId?: string
  sisErrorMessage?: string
}

type ScribeSisErrorDetailItemVector = {
  type: 'line' | 'column' | 'length'
  value: string
}

type ScribeSisErrorDetail = {
  code: string
  message: string
  itemVector: ScribeSisErrorDetailItemVector[]
}

type ScribeSisError = {
  description: string,
  overridable: boolean,
  type: string
  details: ScribeSisErrorDetail[]
}

type SyncStep = {
  status: string,
  errors?: {
    error?: string,
    errorDetails?: { [key in string]: { body?: { errors?: ScribeSisError[] }, postType: string }[] }
  }[],
  misc?: {
    updates?: {
      [key in string]?: {
        postType: string,
        metadata: {
          createdRequisiteId?: string
        }
      }[]
    }
  }
}

type ScribeValidation = {
  key: string,
  description: string,
}

type ScribeValidations = {
  [key in SCRIBE_VALIDATIONS]: ScribeValidation[]
}

type ValidationConfig = { validationId?: SCRIBE_VALIDATIONS, sort?: 'asc' | 'desc' | 'inverse' }

const scribeQuestions: FormQuestion[] = [
  {
    questionId: 'blockType',
    dataKey: 'blockType',
    inputType: 'select',
    label: 'Block Type',
    placeholder: 'Enter Block Type',
    required: true,
  },
  {
    questionId: 'blockValueText',
    dataKey: 'blockValue',
    inputType: 'text',
    label: 'Block Value',
    placeholder: 'Enter Block Value',
    hidden: false,
    required: true,
    __forceComputed: true,
  },
  {
    questionId: 'blockValue',
    dataKey: 'blockValue',
    inputType: 'select',
    label: 'Block Value',
    placeholder: 'Select Block Type',
    hidden: true,
    required: true,
    __forceComputed: true,
  },
  {
    questionId: 'catalogStart',
    dataKey: 'catalogStart',
    inputType: 'select',
    label: 'Catalog Year Start',
    placeholder: 'Enter Catalog Year Start',
    required: true,
  },
  {
    questionId: 'catalogStop',
    dataKey: 'catalogStop',
    inputType: 'select',
    label: 'Catalog Year Stop',
    placeholder: 'Enter Catalog Year Stop',
    required: true,
  },
]

export default defineComponent({
  components: {
    DynamicForm,
    CommonCodeEditor,
  },
  props: {
    scribeErrors: {
      type: Array as () => ScribeError[],
      default: () => [],
    },
    scribeBlock: {
      type: String,
      default: '',
    },
    sisRequisite: {
      type: Object,
      default: () => ({ requirementText: '' }),
    },
    requisite: {
      type: Object,
      default: () => {},
    },
    school: {
      type: String,
      default: '',
    },
    program: {
      type: Object,
      default: () => {},
    },
    scribeValidations: {
      type: Object,
      default: () => {},
    },
  },
  data () {
    return {
      scribeCreateDisabled: this.$v.$invalid,
      scribeSyncDisabled: this.$v.$invalid,
      isScribeCreating: false,
      isScribeSyncing: false,
      sisErrors: [] as ScribeSisErrorDetail[],
      selectedBlockType: undefined,
    }
  },
  setup (props) {
    const { scribeErrors } = toRefs(props)
    const showAllScribeErrors = ref(false)
    const scribeValue = ref(props.sisRequisite.requirementText)
    const scribeValidations = ref(props.scribeValidations as ScribeValidations)

    const scribeErrorsTruncated = computed(() => showAllScribeErrors.value
      ? scribeErrors.value
      : scribeErrors.value.slice(0, 1)
    )

    function onCopySuccess () {
      store.dispatch('ui/showSnackbar', {
        text: 'Scribe copied to clipboard.',
        variant: 'info',
      })
    }

    function updateScribeValue (value: string) {
      scribeValue.value = value
    }

    let scribeFieldsValue = ref({})
    if (props.sisRequisite.primaryTags) {
      scribeQuestions.find(question => question.questionId === 'blockValueText')!.hidden = true
      scribeQuestions.find(question => question.questionId === 'blockValue')!.hidden = false

      scribeFieldsValue = ref(Object.fromEntries(Object.entries(props.sisRequisite.primaryTags).map(([ k, v ]) => {
        const keyRemap = {
          periodStart: 'catalogStart',
          periodStop: 'catalogStop',
        }
        return [
          // @ts-ignore
          keyRemap[k] ?? k,
          {
            // @ts-ignore
            key: v.code,
            // @ts-ignore
            description: v.description,
          },
        ]
      })))
    }

    const scribeFieldsValueByKey = computed(() => {
      const scribeFieldsValueByKey: {[key in string]: string} = {}
      for (const questionId of Object.keys(scribeFieldsValue.value)) {
        const value = (scribeFieldsValue.value as { [key in string]: ScribeValidation | string })[questionId]
        scribeFieldsValueByKey[questionId] = typeof value === 'string' ? value : value.key
      }
      return scribeFieldsValueByKey
    })

    function getValidationIdByQuestionId (questionId: string): ValidationConfig {
      const validationConfig: ValidationConfig = {}
      switch (questionId) {
        case 'blockType':
          validationConfig.sort = 'asc'
          validationConfig.validationId = SCRIBE_VALIDATIONS.BLOCK_TYPES
          break
        case 'blockValue':
          validationConfig.sort = 'asc'
          const blockTypeValidationId = BLOCK_VALUES[scribeFieldsValueByKey.value.blockType as keyof typeof BLOCK_VALUES] as keyof typeof SCRIBE_VALIDATIONS
          if (blockTypeValidationId) validationConfig.validationId = SCRIBE_VALIDATIONS[blockTypeValidationId]
          break
        case 'catalogStart':
        case 'catalogStop':
          validationConfig.sort = 'inverse'
          validationConfig.validationId = SCRIBE_VALIDATIONS.CATALOG_YEAR
          break
      }
      return validationConfig
    }

    function getSortedOptions (validationConfig: ValidationConfig): ScribeValidation[] {
      if (!validationConfig.validationId) throw new Error('No validation Id set at getSortedOptions')
      const options = _.cloneDeep(scribeValidations.value[validationConfig.validationId])
      if (!validationConfig.sort) return options
      if (validationConfig.sort === 'inverse') return _.reverse(options)
      return _.orderBy(options, [ 'description' ], [ validationConfig.sort ])
    }

    for (const scribeQuestion of scribeQuestions) {
      scribeQuestion.customLabel = scribeValidation => scribeValidation.description
      const validationConfig = getValidationIdByQuestionId(scribeQuestion.questionId)
      scribeQuestion.config = { options: validationConfig.validationId ? getSortedOptions(validationConfig) : [] }
    }

    const scribeFieldsQuestions = computed(() => scribeQuestions)
    const scribeFieldsTemplate = scribeFieldsQuestions.value.map(question => question.questionId)
    const { formValue } = useDynamicForm({
      questions: scribeFieldsQuestions.value,
      template: scribeFieldsTemplate,
      formValue: scribeFieldsValue,
      formId: 'scribe-fields-form',
    })

    // @ts-expect-error
    const $v = useVuelidate()

    return {
      scribeValue,
      scribeErrorsTruncated,
      showAllScribeErrors,
      onCopySuccess,
      updateScribeValue,
      scribeFieldsValue,
      scribeFieldsQuestions,
      scribeFieldsValueByKey,
      getValidationIdByQuestionId,
      getSortedOptions,
      formValue,
      $v,
    }
  },
  computed: {
    mergeObjectId () {
      return this.requisite.sisId || NEW_PROGRAM_REQUISITE_ID
    },
    requisiteHasSisId () {
      return !!this.requisite.sisId
    },
    syntaxErrors () {
      return this.sisErrors.filter(error => error.itemVector.length !== 0).map(sisError => {
        const line = sisError.itemVector.find(vector => vector.type === 'line')?.value as string
        const start = sisError.itemVector.find(vector => vector.type === 'column')?.value as string
        const end = sisError.itemVector.find(vector => vector.type === 'length')?.value as string
        if (line === undefined) return
        return {
          line,
          start,
          end: parseInt(start) - 1 + parseInt(end),
          message: `ERROR (${sisError.code}): ${sisError.message}`,
        }
      }).filter(Boolean)
    },
  },
  watch: {
    'scribeFieldsValue.blockType': {
      deep: true,
      handler (newBlockType) {
        if (newBlockType === this.selectedBlockType) return
        this.selectedBlockType = newBlockType
        const blockValueQuestion = this.scribeFieldsQuestions.find(question => question.questionId === 'blockValue')
        const blockValueTextQuestion = this.scribeFieldsQuestions.find(question => question.questionId === 'blockValueText')
        if (newBlockType) {
          const validationConfig = this.getValidationIdByQuestionId('blockValue')
          if (validationConfig.validationId) {
            blockValueQuestion!.hidden = false
            blockValueTextQuestion!.hidden = true
            Vue.set(this.scribeFieldsValue, 'blockValue', { description: '' })
            blockValueQuestion!.config = { options: this.getSortedOptions(validationConfig) }
          } else {
            blockValueQuestion!.hidden = true
            blockValueTextQuestion!.hidden = false
            Vue.set(this.scribeFieldsValue, 'blockValue', '')
            blockValueQuestion!.config = { options: [] }
          }
        }
      },
    },
  },
  methods: {
    getAlertMessage (scribeError: ScribeError) {
      switch (scribeError.errorCode) {
        case 'COURSE_NOT_FOUND':
          return `Course with ID <strong>${scribeError.courseId}</strong> could not be found.`
        case 'COURSE_SET_NOT_FOUND':
          return `Course set with ID <strong>${scribeError.courseSetId}</strong> could not be found.`
        case 'CONDITION_NOT_SUPPORTED':
          return `Condition <strong>${scribeError.condition}</strong> could not be converted to Scribe.`
        case 'SCRIBE_NOT_FOUND':
          return `No requirement text has been found for this requirement. Please ensure to set a valid requirement's sisId or add the following details to create a new block.`
        case 'SYNC_ERROR':
          return `Scribe could not be synced with SIS${scribeError.sisErrorMessage ? `: ${scribeError.sisErrorMessage}` : '.'}`
        case 'SYNC_SUCCESS':
          return `Scribe synced with SIS.`
      }
    },
    getMergeReportSyncStep (mergeReport: MergeReport): SyncStep {
      return mergeReport.steps.find(step => step.id === 'post') as SyncStep
    },
    removeMessageFromCode (code: string) {
      let index = 0
      while (index !== -1) {
        index = this.scribeErrors.findIndex(error => error.errorCode === code)
        if (index !== -1) this.scribeErrors.splice(index, 1)
      }
    },
    setMessage (scribeSyncResponse: ScribeSyncResponse) {
      this.sisErrors = scribeSyncResponse.errors.flatMap(error => error.details)
      const codeToSet = scribeSyncResponse.mergeReportStatus === 'success' ? 'SYNC_SUCCESS' : 'SYNC_ERROR'
      const codeToRemove = scribeSyncResponse.mergeReportStatus === 'success' ? 'SYNC_ERROR' : 'SYNC_SUCCESS'

      for (const error of this.sisErrors) {
        this.scribeErrors.unshift({
          errorCode: codeToSet,
          sisErrorMessage: error.message,
        })
      }
      this.removeMessageFromCode(codeToRemove)
    },
    async syncToScribe (event: Event) {
      const buttonName = (event.target as HTMLButtonElement).name
      // @ts-expect-error
      this.$v.$validate()
      if (this.$v.$invalid) return
      if (buttonName === 'createBlock') this.isScribeCreating = true
      if (buttonName === 'syncToScribe') this.isScribeSyncing = true
      this.scribeCreateDisabled = true
      this.scribeSyncDisabled = true
      const response = await postScribeSync({
        forceCreate: buttonName === 'createBlock',
        school: this.school,
        programId: this.program.id,
        requisiteId: this.requisite.id,
        requisiteSisId: this.requisite.sisId,
        scribeBlock: this.scribeValue,
        blockTitle: this.requisite.name,
        ...this.scribeFieldsValueByKey,
      }) as unknown as ScribeSyncResponse
      this.setMessage(response)
      if (buttonName === 'createBlock' || !this.requisiteHasSisId) this.requisite.sisId = response.createdRequisiteId
      this.isScribeCreating = false
      this.isScribeSyncing = false
      this.scribeCreateDisabled = this.$v.$invalid
      this.scribeSyncDisabled = this.$v.$invalid
    },
    redirectPath (reportId: string) {
      return `/#/admin/${this.school}/merge-dashboard/${reportId}`
    },
    viewMergeReportDetailsAnchorTag (mergeReportId?: string): string {
      if (!mergeReportId) return ''
      return `<a href="${this.redirectPath(mergeReportId)}">View report details</a>`
    },
  },
})
